import * as THREE from "three";
import { U } from "../streetScene.js";
import { kaponHeightAt } from "../kaponTerrain.js";
import { CharBillboard } from "../charBillboard.js";
import { DEFAULT_APPEARANCE, type CharacterAppearance, type Guard } from "@ghostland/shared";

/**
 * Renders the server-authoritative friendly GATE GUARDS (combat NPCs) in Kapon. Each guard is a
 * humanoid `CharBillboard` (same paperdoll renderer as players/NPCs) driven entirely by the synced
 * `Guard` schema: position + heading + state come from the server; this class mirrors + interpolates
 * them, plays the sword swing on the "attack" state edge, lays the guard down when downed, and shows a
 * health bar. Like other NPCs they carry NO floating name (the name shows in the target frame only
 * when selected) but ARE click/Tab selectable. Guards sit on the Kapon terrain via `kaponHeightAt`.
 */

const LERP = 0.25;       // position smoothing toward the authoritative spot

/** The gate-guard look: dark uniform + cap (mirrors the city gate guards Bryce/Hale). */
const GUARD_LOOK: CharacterAppearance = {
  ...DEFAULT_APPEARANCE,
  // top: TEE (style 1) — has attack/block sheets so the uniform stays on during the swing (the tech
  // top, style 4, had no attack art and vanished mid-attack). topColor 1 = black uniform.
  face: 0, skinTone: 4, hair: 3, hairColor: 1, top: 1, topColor: 1,
  bottom: 1, bottomColor: 1, shoes: 1, shoesColor: 1, hat: 1, hatColor: 1,
};

interface GuardEntity {
  bb: CharBillboard;
  x: number; z: number;            // interpolated render position (world units)
  serverX: number; serverZ: number; // authoritative target (world units)
  heading: number; moving: boolean; state: string; health: number; maxHealth: number; name: string;
  lastHp: number;
}

export class GuardController {
  private readonly guards = new Map<string, GuardEntity>();
  private visible = true;

  constructor(private readonly scene: THREE.Scene) {}

  add(key: string, g: Guard): void {
    if (this.guards.has(key)) { this.sync(key, g); return; }
    const bb = new CharBillboard();
    bb.setEquippedHands(true, false);          // a sword in hand (set before appearance → one composite)
    void bb.setAppearance(GUARD_LOOK);
    bb.group.visible = this.visible;
    bb.setPosition(g.x * U, kaponHeightAt(g.x * U, g.y * U), g.y * U);
    this.scene.add(bb.group);
    const e: GuardEntity = {
      bb, x: g.x * U, z: g.y * U, serverX: g.x * U, serverZ: g.y * U,
      heading: g.heading, moving: g.moving, state: g.state, health: g.health, maxHealth: g.maxHealth,
      name: g.name || "Gate Guard", lastHp: -1,
    };
    this.guards.set(key, e);
    bb.setHealth(g.health, g.maxHealth);
  }

  /** Mirror the synced schema fields (called on every Guard onChange). */
  sync(key: string, g: Guard): void {
    const e = this.guards.get(key); if (!e) return;
    e.serverX = g.x * U; e.serverZ = g.y * U;
    e.heading = g.heading; e.moving = g.moving; e.health = g.health; e.maxHealth = g.maxHealth;
    if (g.state !== e.state) {
      // Rising edge into "attack" → play the one-shot swing; into "dead" → flop down.
      if (g.state === "attack") e.bb.playAttack();
      e.bb.setDead(g.state === "dead");
      e.state = g.state;
    }
  }

  /** Flash + shake a guard that just took damage (the "got hit" feel). */
  hit(key: string): void { this.guards.get(key)?.bb.hit(); }

  /** Pixel-accurate pick of a guard billboard under the pick ray (for click-to-select). */
  pick(ray: THREE.Raycaster): string | null {
    const live = [...this.guards.entries()].filter(([, e]) => e.bb.group.visible && e.state !== "dead");
    const hits = ray.intersectObjects(live.map(([, e]) => e.bb.group), true);
    for (const h of hits) {
      if (!h.uv) continue;
      const ent = live.find(([, e]) => e.bb.group === h.object.parent);
      if (ent && ent[1].bb.hitTestUV(h.uv)) return ent[0];
    }
    return null;
  }

  /** Live guards within `range` world units of (wx,wz), nearest first — for Tab targeting. */
  inRange(wx: number, wz: number, range: number): { key: string; x: number; z: number }[] {
    const r2 = range * range, out: { key: string; x: number; z: number }[] = [];
    for (const [key, e] of this.guards) {
      if (e.state === "dead") continue;
      const dx = e.x - wx, dz = e.z - wz;
      if (dx * dx + dz * dz <= r2) out.push({ key, x: e.x, z: e.z });
    }
    out.sort((a, b) => (a.x - wx) ** 2 + (a.z - wz) ** 2 - ((b.x - wx) ** 2 + (b.z - wz) ** 2));
    return out;
  }

  /** Target-frame info for a guard (name + health + alive flag), or null if gone. */
  infoOf(key: string): { name: string; health: number; maxHealth: number; alive: boolean } | null {
    const e = this.guards.get(key);
    return e ? { name: e.name, health: e.health, maxHealth: e.maxHealth, alive: e.state !== "dead" } : null;
  }

  /** Live render position (world units) of a guard, or null — for the ground selection ring. */
  posOf(key: string): { x: number; z: number } | null {
    const e = this.guards.get(key);
    return e ? { x: e.x, z: e.z } : null;
  }

  remove(key: string): void {
    const e = this.guards.get(key); if (!e) return;
    this.scene.remove(e.bb.group); e.bb.dispose();
    this.guards.delete(key);
  }

  setVisible(v: boolean): void {
    if (v === this.visible) return;
    this.visible = v;
    for (const e of this.guards.values()) e.bb.group.visible = v;
  }

  tick(dt: number, camYaw: number, visible: boolean): void {
    this.setVisible(visible);
    if (!visible) return;
    const dtMs = dt * 1000;
    for (const e of this.guards.values()) {
      e.x += (e.serverX - e.x) * LERP;
      e.z += (e.serverZ - e.z) * LERP;
      e.bb.setPosition(e.x, kaponHeightAt(e.x, e.z), e.z);
      e.bb.setHeading(e.heading, e.moving && e.state !== "dead", false);
      if (e.health !== e.lastHp) { e.bb.setHealth(e.health, e.maxHealth); e.lastHp = e.health; }
      e.bb.tick(dtMs, camYaw);
    }
  }

  dispose(): void {
    for (const key of [...this.guards.keys()]) this.remove(key);
  }
}
